// frontend/src/marketing/PublicHeader.jsx
import React, { Fragment, useContext } from "react";
import { Link, NavLink } from "react-router-dom";
import { Popover, Transition, Disclosure } from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { PRIMARY_NAV } from "./navConfig";
import { Button, cx } from "./ui";
import Logo from "./Logo";
import AuthContext from "../context/AuthContext";

/**
 * Read auth state without throwing when no provider is mounted (the public
 * header is also rendered in isolation by the link-integrity test). Returns
 * true only when a real session is present.
 */
function useIsAuthed() {
  const ctx = useContext(AuthContext);
  return Boolean(ctx?.user);
}

/* Desktop dropdown for a nav item that has children. */
function NavDropdown({ label, items }) {
  return (
    <Popover className="relative">
      <Popover.Button className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 outline-none hover:text-brand-700 focus-visible:text-brand-700">
        {label}
        <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
      </Popover.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Popover.Panel className="absolute left-1/2 z-20 mt-3 w-80 -translate-x-1/2 rounded-2xl bg-white p-2 shadow-lg ring-1 ring-gray-200">
          {({ close }) => (
            <ul>
              {items.map((c) => (
                <li key={c.to}>
                  <Link
                    to={c.to}
                    onClick={() => close()}
                    className="block rounded-xl p-3 hover:bg-gray-50"
                  >
                    <span className="block text-sm font-semibold text-gray-900">{c.label}</span>
                    {c.desc && <span className="block text-xs text-gray-500">{c.desc}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}

export default function PublicHeader() {
  const isAuthed = useIsAuthed();
  return (
    <Disclosure as="header" className="sticky top-0 z-30 border-b border-gray-200 bg-white/90 backdrop-blur">
      {({ open }) => (
        <>
          <nav className="mx-auto flex max-w-container items-center justify-between gap-6 px-4 py-4 sm:px-6 lg:px-8">
            <Link to="/" className="flex items-center gap-2" aria-label="QRFlow home">
              <Logo className="h-8 w-auto" />
            </Link>

            {/* Desktop nav */}
            <div className="hidden items-center gap-7 lg:flex">
              {PRIMARY_NAV.map((item) =>
                item.children ? (
                  <NavDropdown key={item.label} label={item.label} items={item.children} />
                ) : (
                  <NavLink
                    key={item.label}
                    to={item.to}
                    className={({ isActive }) =>
                      cx(
                        "text-sm font-medium hover:text-brand-700",
                        isActive ? "text-brand-700" : "text-gray-700",
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ),
              )}
            </div>

            <div className="hidden items-center gap-3 lg:flex">
              {isAuthed ? (
                <Button to="/dashboard">Go to dashboard</Button>
              ) : (
                <>
                  <Button to="/login" variant="ghost">Log in</Button>
                  <Button to="/register">Get started free</Button>
                </>
              )}
            </div>

            {/* Mobile toggle */}
            <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-700 lg:hidden">
              <span className="sr-only">Toggle menu</span>
              {open ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </Disclosure.Button>
          </nav>

          {/* Mobile panel */}
          <Disclosure.Panel className="border-t border-gray-200 bg-white lg:hidden">
            <div className="space-y-1 px-4 py-4">
              {PRIMARY_NAV.map((item) =>
                item.children ? (
                  <div key={item.label} className="py-1">
                    <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {item.label}
                    </p>
                    {item.children.map((c) => (
                      <Disclosure.Button
                        as={Link}
                        key={c.to}
                        to={c.to}
                        className="block rounded-lg px-2 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {c.label}
                      </Disclosure.Button>
                    ))}
                  </div>
                ) : (
                  <Disclosure.Button
                    as={Link}
                    key={item.label}
                    to={item.to}
                    className="block rounded-lg px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {item.label}
                  </Disclosure.Button>
                ),
              )}
              <div className="mt-3 flex flex-col gap-2 border-t border-gray-200 pt-4">
                {isAuthed ? (
                  <Button to="/dashboard">Go to dashboard</Button>
                ) : (
                  <>
                    <Button to="/login" variant="secondary">Log in</Button>
                    <Button to="/register">Get started free</Button>
                  </>
                )}
              </div>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
